import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/context/AuthContext';
import { Toaster } from 'sonner';
import SplashExperience from '@/components/SplashExperience';
import BrandEntrance from '@/components/BrandEntrance';
import EntranceMusicControl from '@/components/EntranceMusicControl';
import InstallPrompt, { SeoRouteMeta } from '@/components/InstallPrompt';

import Landing from '@/pages/Landing';
import Features from '@/pages/Features';
import About from '@/pages/About';
import FAQ from '@/pages/FAQ';
import NotFound from '@/pages/NotFound';
import Privacy from '@/pages/Privacy';
import Terms from '@/pages/Terms';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import Onboarding from '@/pages/Onboarding';
import Assessment from '@/pages/Assessment';
import AssessmentResults from '@/pages/AssessmentResults';
import CheckoutStart from '@/pages/CheckoutStart';
import TherapistIntake from '@/pages/TherapistIntake';
import PublicDirectory from '@/pages/PublicDirectory';
import PublicTherapistProfile from '@/pages/PublicTherapistProfile';

import AppShell from '@/components/AppShell';
import Home from '@/pages/Home';
import Coach from '@/pages/Coach';
import Learn from '@/pages/Learn';
import LessonDetail from '@/pages/LessonDetail';
import SupportingMen from '@/pages/SupportingMen';
import Journal from '@/pages/Journal';
import Progress from '@/pages/Progress';
import Therapists from '@/pages/Therapists';
import Profile from '@/pages/Profile';
import TrustSafety from '@/pages/TrustSafety';
import Admin from '@/pages/Admin';
import DailyCheckin from '@/pages/DailyCheckin';
import GrowthPlans from '@/pages/GrowthPlans';
import PlanDetail from '@/pages/PlanDetail';
import Events from '@/pages/Workshops';
import Resources from '@/pages/Resources';
import CrisisPage from '@/pages/CrisisPage';
import Podcast from '@/pages/Podcast';
import HelpCenter from '@/pages/HelpCenter';
import Lounge from '@/pages/Lounge';
// Organizations page removed in Iter 47 — feature deprecated.
import Support from '@/pages/Support';
import SupportGuide from '@/pages/SupportGuide';
import SupportCoach from '@/pages/SupportCoach';
import SupportHomePage from '@/pages/SupportHome';
import SupportCrisis from '@/pages/SupportCrisis';
import SupportUniversity from '@/pages/SupportUniversity';
import SupportCourse from '@/pages/SupportCourse';
import RequireRole from '@/components/RequireRole';
import AttachmentHub from '@/pages/AttachmentHub';
import AttachmentAssessment from '@/pages/AttachmentAssessment';
import AttachmentResults from '@/pages/AttachmentResults';
import FeatureVoting from '@/pages/FeatureVoting';
import AdminAnalytics from '@/pages/AdminAnalytics';
import AdminMemberInsights from '@/pages/AdminMemberInsights';
import Memory from '@/pages/Memory';
import VerifyEmail from '@/pages/VerifyEmail';
import Sessions from '@/pages/Sessions';
import AdminSecurity from '@/pages/AdminSecurity';
import AdminEmailLog from '@/pages/AdminEmailLog';
import AdminUsers from '@/pages/AdminUsers';
import AdminAIUsage from '@/pages/AdminAIUsage';
import AdminDeletionLog from '@/pages/AdminDeletionLog';
import AdminPromoCodes from '@/pages/AdminPromoCodes';
import AdminFeedback from '@/pages/AdminFeedback';
import AdminContact from '@/pages/AdminContact';
import Community from '@/pages/Community';
import AdminCommunity from '@/pages/AdminCommunity';
import AdminTherapists from '@/pages/AdminTherapists';
import OwnerBootstrap from '@/pages/OwnerBootstrap';
import EmbeddedGame from '@/pages/EmbeddedGame';
import SupportContact from '@/pages/SupportContact';

export default function App() {
  // Public utility pages reached via a shared direct link — skip the app entrance/splash gate.
  const _isBarePublic = (() => {
    try { return /^\/(directory|therapists|therapist-apply|therapist-intake|therapist-manage)(\/|$)/.test(window.location.pathname); }
    catch { return false; }
  })();
  const [showEntrance, setShowEntrance] = useState(() => {
    if (_isBarePublic) return false;
    try { return !sessionStorage.getItem('bh_entrance_shown'); } catch { return true; }
  });
  const [entered, setEntered] = useState(false);
  const finishEntrance = () => {
    try { sessionStorage.setItem('bh_entrance_shown', '1'); } catch { /* ignore */ }
    setShowEntrance(false);
  };
  const [showSplash, setShowSplash] = useState(() => {
    if (_isBarePublic) return false;
    try { return !sessionStorage.getItem('bh_splash_shown'); } catch { return true; }
  });
  const finishSplash = () => {
    try { sessionStorage.setItem('bh_splash_shown', '1'); } catch { /* ignore */ }
    setShowSplash(false);
  };
  // Mark splash as shown once it mounts, so a reload mid-splash won't replay it.
  useEffect(() => {
    if (entered && showSplash) { try { sessionStorage.setItem('bh_splash_shown', '1'); } catch { /* ignore */ } }
  }, [entered]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <AuthProvider>
      <BrowserRouter>
        {entered && showSplash && <SplashExperience onFinish={finishSplash} />}
        {showEntrance && <BrandEntrance onEnter={() => setEntered(true)} onExited={finishEntrance} />}
        <EntranceMusicControl />
        <SeoRouteMeta />
        <InstallPrompt />
        <Toaster position="top-center" richColors />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/features" element={<Features />} />
          <Route path="/about" element={<About />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/404" element={<NotFound />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/support" element={<SupportContact />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/owner-bootstrap" element={<OwnerBootstrap />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/assessment" element={<Assessment />} />
          <Route path="/assessment/results" element={<AssessmentResults />} />
          <Route path="/subscription/checkout" element={<CheckoutStart />} />
          <Route path="/therapist-intake/:token" element={<TherapistIntake />} />
          <Route path="/therapist-apply" element={<TherapistIntake />} />
          <Route path="/therapist-manage/:token" element={<TherapistIntake mode="manage" />} />
          <Route path="/directory" element={<PublicDirectory />} />
          <Route path="/therapists/:slug" element={<PublicTherapistProfile />} />
          <Route path="/crisis" element={<CrisisPage />} />
          <Route path="/embed/lounge/:game" element={<EmbeddedGame />} />
          <Route path="/app" element={<AppShell />}>
            <Route index element={<RequireRole roles={['member','admin']}><Home /></RequireRole>} />
            <Route path="coach" element={<RequireRole roles={['member','admin']}><Coach /></RequireRole>} />
            <Route path="learn" element={<RequireRole roles={['member','admin']}><Learn /></RequireRole>} />
            <Route path="learn/lesson/:id" element={<RequireRole roles={['member','admin']}><LessonDetail /></RequireRole>} />
            <Route path="learn/supporting-men" element={<SupportingMen />} />
            <Route path="journal" element={<RequireRole roles={['member','admin']}><Journal /></RequireRole>} />
            <Route path="progress" element={<RequireRole roles={['member','admin']}><Progress /></RequireRole>} />
            <Route path="therapists" element={<Therapists />} />
            <Route path="community" element={<RequireRole roles={['member','admin']}><Community community="brotherhood" /></RequireRole>} />
            <Route path="support-community" element={<RequireRole roles={['supporter','admin']}><Community community="supporter" /></RequireRole>} />
            <Route path="admin/community" element={<AdminCommunity />} />
            <Route path="admin/therapists" element={<AdminTherapists />} />
            <Route path="profile" element={<Profile />} />
            <Route path="profile/sessions" element={<Sessions />} />
            <Route path="admin/security" element={<AdminSecurity />} />
            <Route path="admin/email-log" element={<AdminEmailLog />} />
            <Route path="admin/users" element={<AdminUsers />} />
            <Route path="admin/deletions" element={<AdminDeletionLog />} />
            <Route path="admin/promo-codes" element={<AdminPromoCodes />} />
            <Route path="admin/ai-usage" element={<AdminAIUsage />} />
            <Route path="admin/beta-feedback" element={<AdminFeedback />} />
            <Route path="admin/contact" element={<AdminContact />} />
            <Route path="profile/trust-safety" element={<TrustSafety />} />
            <Route path="admin" element={<Admin />} />
            <Route path="checkin" element={<RequireRole roles={['member','admin']}><DailyCheckin /></RequireRole>} />
            <Route path="plans" element={<RequireRole roles={['member','admin']}><GrowthPlans /></RequireRole>} />
            <Route path="plans/:id" element={<RequireRole roles={['member','admin']}><PlanDetail /></RequireRole>} />
            <Route path="workshops" element={<Events />} />
            <Route path="resources" element={<Resources />} />
            <Route path="lounge" element={<RequireRole roles={['member','admin']}><Lounge /></RequireRole>} />
            <Route path="podcast" element={<Podcast />} />
            {/* organizations route removed in Iter 47 */}
            <Route path="support" element={<Support />} />
            <Route path="support/coach" element={<SupportCoach />} />
            <Route path="support/:guideId" element={<SupportGuide />} />
            {/* ─── Brotherhood Support (loved-ones product) shell ─── */}
            <Route path="support-home" element={<RequireRole roles={['supporter','admin']}><SupportHomePage /></RequireRole>} />
            <Route path="support-coach" element={<RequireRole roles={['supporter','admin']}><SupportCoach /></RequireRole>} />
            <Route path="support-library" element={<Navigate to="/app/support-university?tab=guides" replace />} />
            <Route path="support-library/:guideId" element={<RequireRole roles={['supporter','admin']}><SupportGuide /></RequireRole>} />
            <Route path="support-crisis" element={<SupportCrisis />} />
            <Route path="support-university" element={<RequireRole roles={['supporter','admin']}><SupportUniversity /></RequireRole>} />
            <Route path="support-university/:courseId" element={<RequireRole roles={['supporter','admin']}><SupportCourse /></RequireRole>} />
            <Route path="attachment" element={<RequireRole roles={['member','admin']}><AttachmentHub /></RequireRole>} />
            <Route path="attachment/take" element={<RequireRole roles={['member','admin']}><AttachmentAssessment /></RequireRole>} />
            <Route path="attachment/results" element={<RequireRole roles={['member','admin']}><AttachmentResults /></RequireRole>} />
            <Route path="roadmap" element={<FeatureVoting />} />
            <Route path="admin/analytics" element={<AdminAnalytics />} />
            <Route path="admin/member-insights" element={<AdminMemberInsights />} />
            <Route path="help" element={<HelpCenter />} />
            <Route path="memory" element={<Memory />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
